import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";

const url = (dealId: number, path: string) =>
  `${APIS.Deals}/${dealId}/items/bulk/${path}`;

const lookup = async <T,>(
  dealId: number,
  path: string,
  dealItemIds: number[]
): Promise<T[]> => {
  if (dealItemIds.length === 0) return [];
  const response = await apiClient.get<{ items: T[] }>(url(dealId, path), {
    params: { dealItemIds },
    paramsSerializer: { indexes: null },
  });
  return response.data.items ?? [];
};

export const fetchBulkZones = (dealId: number, ids: number[]) =>
  lookup<number>(dealId, "zones", ids);
export const fetchBulkOverrides = (dealId: number, ids: number[]) =>
  lookup<{ zoneNumber: number; overrideNumber: number }>(
    dealId,
    "overrides",
    ids
  );
export const fetchBulkStores = (dealId: number, ids: number[]) =>
  lookup<number>(dealId, "stores", ids);
export const fetchBulkPromoGroups = (dealId: number, ids: number[]) =>
  lookup<number>(dealId, "promo-groups", ids);

interface BulkRequestBase {
  dealItemIds: number[];
}

const useBulkMutation = <TBody,>(dealId: number, path: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TBody) => {
      const response = await apiClient.post<{ rowsAffected: number }>(
        url(dealId, path),
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-items", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};

export const useBulkDelete = (dealId: number) =>
  useBulkMutation<BulkRequestBase>(dealId, "delete");

export const useBulkRemoveZones = (dealId: number) =>
  useBulkMutation<BulkRequestBase & { zoneNumbers: number[] }>(
    dealId,
    "remove-zones"
  );

export const useBulkRemoveOverrides = (dealId: number) =>
  useBulkMutation<
    BulkRequestBase & {
      overrides: { zoneNumber: number; overrideNumber: number }[];
    }
  >(dealId, "remove-overrides");

export const useBulkRemoveStores = (dealId: number) =>
  useBulkMutation<BulkRequestBase & { storeNumbers: number[] }>(
    dealId,
    "remove-stores"
  );

export const useBulkRemovePromoGroup = (dealId: number) =>
  useBulkMutation<BulkRequestBase & { promoGroupIds: number[] }>(
    dealId,
    "remove-promo-group"
  );

export const useBulkRemoveDealPricing = (dealId: number) =>
  useBulkMutation<
    BulkRequestBase & {
      includeScan: boolean;
      includePromoPrice: boolean;
      includeAllowance: boolean;
    }
  >(dealId, "remove-deal-pricing");

export const useBulkApplyToAll = (dealId: number) =>
  useBulkMutation<{
    sourceDealItemId: number;
    targetDealItemIds: number[];
  }>(dealId, "apply-to-all");
